import { useMemo } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import {
  VolumeTypeEnum,
  type QuarterVolume,
} from "../../../../../store/types/secureVolume.types";
import { DISPLAY_VOLUME_TYPES, VOLUME_TYPE_CONFIG, fmt, pctFmt } from "../types";
import { getQuarterVolumesByType, getQuarterSecuredPercentage } from "../helpers";
import { getSecuredPctColor } from "./SecuredIndicator";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];

function MonthTooltipContent({ volumes, year, quarter }: { volumes: QuarterVolume[]; year: number; quarter: number }) {
  const monthRows = useMemo(() => {
    const startMonth = (quarter - 1) * 3;
    const rows: { month: string; qty: number; type: VolumeTypeEnum; key: string }[] = [];

    for (let m = 0; m < 3; m++) {
      const monthNum = startMonth + m + 1;
      const monthName = MONTH_NAMES[startMonth + m];

      // Group by VolumeType per month
      const byType = new Map<VolumeTypeEnum, number>();
      for (const v of volumes) {
        if (v.Year !== year || v.Quarter !== quarter || !v.MonthDetails?.length) continue;
        for (const md of v.MonthDetails) {
          if (md.Month === monthNum) {
            byType.set(md.VolumeType, (byType.get(md.VolumeType) ?? 0) + md.Quantity);
          }
        }
      }

      for (const [type, qty] of byType) {
        rows.push({ month: monthName, qty, type, key: `${monthNum}-${type}` });
      }
    }

    return rows;
  }, [volumes, year, quarter]);

  if (monthRows.length === 0) return null;

  return (
    <Stack spacing={0.25} sx={{ p: 0.5 }}>
      {monthRows.map((row) => (
        <Box key={row.key} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: VOLUME_TYPE_CONFIG[row.type]?.color ?? "#9e9e9e",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ minWidth: 28 }}>{row.month}</Typography>
          <Typography variant="caption" fontWeight={600}>{fmt.format(row.qty)} kWh</Typography>
          <Typography variant="caption" color="text.secondary">
            {VOLUME_TYPE_CONFIG[row.type]?.label ?? ""}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export function VolumeCellContent({
  volumes,
  year,
  quarter,
  excludeTypes = [],
}: {
  volumes: QuarterVolume[];
  year: number;
  quarter: number;
  excludeTypes?: VolumeTypeEnum[];
}) {
  const typeMap = getQuarterVolumesByType(volumes, year, quarter);
  const entries = DISPLAY_VOLUME_TYPES.filter(
    (t) => !excludeTypes.includes(t) && typeMap.has(t),
  ).map((t) => ({ type: t, qty: typeMap.get(t)! }));

  if (entries.length === 0) {
    return null;
  }

  const secured = typeMap.get(VolumeTypeEnum.Secured) ?? 0;
  const securedPct = secured > 0
    ? getQuarterSecuredPercentage(volumes, year, quarter)
    : null;

  // Check if any MonthDetails exist for this quarter
  const hasMonthDetails = volumes.some(
    (v) => v.Year === year && v.Quarter === quarter && v.MonthDetails && v.MonthDetails.length > 0,
  );

  const content = (
    <Stack spacing={0}>
      {entries.map(({ type, qty }) => {
        const isSecured = type === VolumeTypeEnum.Secured;
        return (
          <Box
            key={type}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: VOLUME_TYPE_CONFIG[type].color,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption">{fmt.format(qty)}</Typography>
            {isSecured && securedPct !== null && (
              <Typography
                variant="caption"
                sx={{
                  color: getSecuredPctColor(securedPct),
                  fontWeight: 600,
                  ml: "auto",
                }}
              >
                {pctFmt.format(securedPct)}%
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );

  if (!hasMonthDetails) return content;

  return (
    <Tooltip
      title={<MonthTooltipContent volumes={volumes} year={year} quarter={quarter} />}
      arrow
      placement="top"
      slotProps={{
        tooltip: { sx: { maxWidth: 300, bgcolor: "grey.900" } },
      }}
    >
      <Box sx={{ cursor: "default" }}>{content}</Box>
    </Tooltip>
  );
}
